import Dexie, { Table } from 'dexie';

export interface Budget {
  id: string; // UUID
  userId?: string; // Google sub ID
  name: string;
  isDefault: boolean;
  createdAt: string; // ISO String
}

export type RecurrenceFrequency =
  'once' | 'weekly' | 'biweekly' | 'semi-monthly' | 'monthly' | 'annually' | 'custom';

export type CustomIntervalUnit = 'days' | 'weeks' | 'months' | 'years';

export interface LoanDetails {
  principalBalance: number;
  annualInterestRate: number;
  minimumPayment: number;
  originationDate?: string;
  termMonths?: number;
  // --- Snowball Metadata ---
  originalEndDate?: string; // Preserves contract maturity date when accelerated
  snowballAccelerated?: boolean; // Indicates early payoff active
}

export interface DbCashItem {
  id: string;
  budgetId: string;
  userId?: string;
  name: string;
  amount: number;
  type: 'income' | 'expense';
  category?:
    'housing' | 'utilities' | 'debt_loan' | 'transportation' | 'food' | 'personal' | 'other';
  isLoan?: boolean;
  loanDetails?: LoanDetails;
  startDate: string; // YYYY-MM-DD
  endDate?: string;
  frequency: RecurrenceFrequency;

  // Custom Cadence
  customIntervalCount?: number; // e.g. 6
  customIntervalUnit?: CustomIntervalUnit; // 'days' | 'weeks' | 'months' | 'years'

  // Clamping and scheduling metadata
  dueDayOfMonth?: number; // 1-31 (used to preserve intended day across short months)
  semiMonthlyDays?: [number, number]; // e.g. [1, 15] or [15, 31]
  adjustForWeekends?: boolean; // Shifts to Friday if date lands on Sat/Sun
}

export interface InstanceOverride {
  id: string; // Composite key: `${cashItemId}_${projectedDate}`
  cashItemId: string;
  budgetId: string;
  projectedDate: string; // YYYY-MM-DD
  isPaid: boolean;
  isSkipped?: boolean; // Excludes this occurrence from budget calculations
  isLocked?: boolean; // Prevents auto-balancing and manual moving/splitting
  actualAmount?: number; // Adjusted amount (e.g. after a split)
  actualDate?: string; // Moved date
  splitChildItemIds?: string[]; // Array of created one-time child item IDs
}

// --- Debt Payoff Plan Models ---
export type PayoffStrategy = 'snowball' | 'avalanche';

export interface PlannedDebtPayoff {
  cashItemId: string;
  debtName: string;
  startingBalance: number;
  annualInterestRate: number;
  minimumPayment: number;
  originalEndDate?: string;
  projectedPayoffDate: string;
  totalInterestPaid: number;
  monthsToPayoff: number;
  payoffOrder: number;
}

export interface DebtMilestone {
  date: string; // YYYY-MM-DD
  monthIndex: number;
  cashItemId: string;
  debtName: string;
  freedMonthlyCash: number;
  totalSnowballAmount: number;
  remainingDebtsCount: number;
}

export interface DebtPayoffPlan {
  id: string; // "active_debt_plan"
  budgetId: string;
  strategy: PayoffStrategy;
  monthlyExtraAmount: number;
  createdAt: string;
  debtFreeDate: string;
  totalInterestSaved: number;
  debts: {
    cashItemId: string;
    targetPayoffDate: string;
    originalEndDate?: string;
  }[];
  generatedItemIds: string[];
}

export interface DbUserSettings {
  id: string;
  budgetId: string;
  userId?: string;
  payPeriodAnchorDate: string; // YYYY-MM-DD
  payPeriodDurationDays: number; // e.g., 7, 14, 15, 30
  lowBalanceThreshold: number;
  enableCarryover: boolean;
  activeDebtPlan?: DebtPayoffPlan | null; // Attached active payoff plan
  activeEmergencyPlan?: EmergencyFundPlan | null;
  activeFindMoneyPlan?: FindMoneyPlan | null;
}

export interface EmergencyFundPlan {
  id: string; // "active_emergency_plan"
  budgetId: string;
  targetMonths: number; // e.g., 3, 6, 9
  currentSavingsBalance: number;
  monthlyContribution: number;
  monthlyExpenseBaseline: number;
  targetGoalAmount: number;
  projectedCompletionDate: string;
  createdAt: string;
  generatedItemIds: string[];
}

export interface FindMoneyPlan {
  id: string; // "active_find_money_plan"
  budgetId: string;
  goalName: string;
  targetAmount: number;
  targetDate?: string; // Optional for ASAP mode
  mode: 'date_driven' | 'asap';
  projectedAchieveDate: string;
  createdAt: string;
  generatedItemIds: string[];
}

export class AppDb extends Dexie {
  budgets!: Table<Budget, string>;
  cashItems!: Table<DbCashItem, string>;
  instanceOverrides!: Table<InstanceOverride, string>;
  userSettings!: Table<DbUserSettings, string>;

  constructor() {
    super('SuiteLifeToolsDb');

    // Schema definition with versioning
    this.version(4).stores({
      budgets: 'id, userId, isDefault',
      cashItems: 'id, budgetId, userId, type, startDate, frequency',
      instanceOverrides: 'id, cashItemId, budgetId, projectedDate, isPaid, isSkipped',
      userSettings: 'id, budgetId, userId',
    });
  }
}

export const db = new AppDb();
